<template>
  <article
    class="v2-event-card v2-card v2-card--clickable"
    :class="{ 'v2-event-card--live': isLive, 'v2-event-card--past': isPast }"
    @click="onClick"
  >
    <div class="v2-event-card__media">
      <img
        v-if="thumbnail"
        :src="thumbnail"
        :alt="title"
        class="v2-event-card__image"
        loading="lazy"
      />
      <img
        v-else
        :src="placeholderImage"
        :alt="`Placeholder para ${title}`"
        class="v2-event-card__image v2-event-card__image--placeholder"
        loading="lazy"
      />

      <span v-if="category" class="v2-event-card__category">
        <v-icon size="12">{{ categoryIcon }}</v-icon>
        {{ category }}
      </span>

      <span v-if="isLive" class="v2-event-card__badge v2-event-card__badge--live">
        <span class="v2-event-card__pulse" />
        En vivo
      </span>
      <span v-else-if="isPast" class="v2-event-card__badge v2-event-card__badge--past">
        Finalizado
      </span>
    </div>

    <div class="v2-event-card__body">
      <header class="v2-event-card__header">
        <div v-if="dateDay" class="v2-event-card__date">
          <span class="v2-event-card__date-day">{{ dateDay }}</span>
          <span class="v2-event-card__date-month">{{ dateMonth }}</span>
        </div>
        <div class="v2-event-card__title-wrap">
          <h3 class="v2-event-card__title">{{ title }}</h3>
          <p v-if="timeLabel" class="v2-event-card__time">
            <v-icon size="12">mdi-clock-outline</v-icon>
            {{ timeLabel }}
          </p>
        </div>
      </header>

      <ul class="v2-event-card__meta">
        <li v-if="location" class="v2-event-card__meta-item">
          <v-icon size="14">{{ typeIcon }}</v-icon>
          <span>{{ location }}</span>
        </li>
        <li v-if="typeLabel" class="v2-event-card__meta-item">
          <v-icon size="14">{{ typeIcon }}</v-icon>
          <span>{{ typeLabel }}</span>
        </li>
      </ul>
    </div>
  </article>
</template>

<script>
import {
  getEventId,
  getEventTitle,
  getEventDescription,
  getEventThumbnail,
  getEventCategory,
  getEventType,
  getEventStartDate,
  getEventLocation,
  isLive,
  resolveEventTypeLabel,
  resolveEventTypeIcon,
  resolveEventDetailRoute,
} from "./eventsHelpers";
import placeholderImage from "@/assets/images/shared/Events_Placeholder.png";
import moment from "moment-timezone";

export default {
  name: "EventCard",
  props: {
    event: { type: Object, required: true },
  },
  data() {
    return {
      placeholderImage,
    };
  },
  computed: {
    id() {
      return getEventId(this.event);
    },
    title() {
      return getEventTitle(this.event);
    },
    description() {
      return getEventDescription(this.event);
    },
    thumbnail() {
      return getEventThumbnail(this.event);
    },
    category() {
      return getEventCategory(this.event);
    },
    typeLabel() {
      return resolveEventTypeLabel(getEventType(this.event));
    },
    typeIcon() {
      return resolveEventTypeIcon(getEventType(this.event));
    },
    location() {
      return getEventLocation(this.event);
    },
    dateDay() {
      const start = getEventStartDate(this.event);
      return start ? moment(start).format("DD") : "";
    },
    dateMonth() {
      const start = getEventStartDate(this.event);
      return start ? moment(start).format("MMM").toUpperCase() : "";
    },
    timeLabel() {
      const start = getEventStartDate(this.event);
      if (!start) return "";
      return moment(start).format("HH:mm") + "h";
    },
    isLive() {
      return isLive(this.event);
    },
    isPast() {
      const start = getEventStartDate(this.event);
      if (!start) return false;
      return moment(start).isBefore(moment(), "day");
    },
    categoryIcon() {
      const cat = (this.category || "").toLowerCase();
      if (cat.includes("taller") || cat.includes("workshop")) return "mdi-hammer-wrench";
      if (cat.includes("networking") || cat.includes("red")) return "mdi-account-group";
      if (cat.includes("conferencia") || cat.includes("conference")) return "mdi-presentation";
      if (cat.includes("social")) return "mdi-glass-cocktail";
      if (cat.includes("formacion") || cat.includes("training")) return "mdi-school";
      return "mdi-tag-outline";
    },
    detailRoute() {
      return resolveEventDetailRoute(this.event);
    },
  },
  methods: {
    onClick() {
      if (!this.id) return;
      this.$router.push(this.detailRoute);
    },
  },
};
</script>

<style scoped>
.v2-event-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  text-align: left;
  cursor: pointer;
  transition: transform var(--transition-base), box-shadow var(--transition-base);
  height: 100%;
}

.v2-event-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-card-hover);
}

.v2-event-card:focus-visible {
  outline: 2px solid var(--color-amber);
  outline-offset: 2px;
}

.v2-event-card__media {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background-color: var(--color-neutral-100);
  overflow: hidden;
}

.v2-event-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform var(--transition-base);
}

.v2-event-card__image--placeholder {
  object-fit: cover;
  background-color: var(--color-neutral-100);
}

.v2-event-card:hover .v2-event-card__image {
  transform: scale(1.04);
}

.v2-event-card__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-surface-low);
  color: var(--color-text-muted);
}

.v2-event-card__category {
  position: absolute;
  top: var(--space-3);
  left: var(--space-3);
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-3);
  background-color: var(--color-amber-soft-2);
  color: var(--color-amber-on);
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  border-radius: var(--radius-full);
  backdrop-filter: blur(8px);
}

.v2-event-card__badge {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-3);
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-bold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  border-radius: var(--radius-full);
  line-height: 1;
}

.v2-event-card__badge--live {
  background-color: var(--color-error);
  color: var(--color-error-on);
}

.v2-event-card__badge--past {
  background-color: var(--color-neutral-700);
  color: var(--color-text-inverse);
}

.v2-event-card__pulse {
  width: 6px;
  height: 6px;
  border-radius: var(--radius-full);
  background-color: currentColor;
  animation: v2-event-card-pulse 1.2s ease-in-out infinite;
}

@keyframes v2-event-card-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.8); }
}

.v2-event-card__body {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  flex: 1 1 auto;
}

.v2-event-card__header {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}

.v2-event-card__date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  padding: var(--space-2) var(--space-3);
  background-color: var(--color-primary-soft);
  color: var(--color-primary);
  border-radius: var(--radius-md);
  text-align: center;
  flex-shrink: 0;
}

.v2-event-card__date-day {
  font-family: var(--font-headline);
  font-size: var(--fs-display-md);
  font-weight: var(--fw-bold);
  line-height: 1;
}

.v2-event-card__date-month {
  font-family: var(--font-body);
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  margin-top: var(--space-1);
}

.v2-event-card__title-wrap {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
  flex: 1 1 auto;
}

.v2-event-card__title {
  font-family: var(--font-headline);
  font-size: var(--fs-body-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  line-height: var(--lh-snug);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.v2-event-card__time {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0;
  font-size: var(--fs-body-sm);
  color: var(--color-text-muted);
}

.v2-event-card__description {
  font-size: var(--fs-body-sm);
  color: var(--color-text-muted);
  line-height: var(--lh-normal);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.v2-event-card__meta {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin-top: auto;
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-border);
}

.v2-event-card__meta-item {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--fs-body-sm);
  color: var(--color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v2-event-card__meta-item :first-child {
  flex-shrink: 0;
}

.v2-event-card__meta-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v2-event-card--past {
  opacity: 0.7;
}

.v2-event-card--past:hover {
  transform: none;
}
</style>
